import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShieldCheck, Truck, Zap, Loader2, ArrowRight, Waves, Battery } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import api, { formatApiError } from "@/lib/api";
import { HUME } from "@/lib/biohackerBrand";

/**
 * Hume Band — Shop page.
 *
 * Sells the official BioHacker Sports™ physiological intelligence device
 * for $209. Backed by ``/api/shop/hume/checkout`` which creates a Stripe
 * Checkout session. Guest checkout — no login required so a coach or
 * parent can buy without an account.
 */
export default function HumeShop() {
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [warranty, setWarranty] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", email: "", ship_country: "United States", notes: "" });
  const [busy, setBusy] = useState(false);
  const [activeImg, setActiveImg] = useState(HUME.images.hero);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/shop/hume"); setProduct(data); }
      catch { /* fall back to static constants */ }
    })();
  }, []);

  // Tier-based unit price — mirrors the backend HUME_TIERS ladder.
  const tiers = product?.tiers || HUME.tiers;
  const activeTier = tiers.find((t) => qty >= t.min_qty && (t.max_qty == null || qty <= t.max_qty)) || tiers[0];
  const unit = activeTier?.unit_price_usd ?? (product?.unit_price_usd ?? HUME.price_usd);
  const baseUnit = tiers?.[0]?.unit_price_usd ?? (product?.unit_price_usd ?? HUME.price_usd);
  const savingsPerBand = +(baseUnit - unit).toFixed(2);
  const warrantyPer = product?.warranty_upgrade_usd ?? 50;
  const subtotal = +(unit * qty).toFixed(2);
  const warrantyTotal = warranty ? +(warrantyPer * qty).toFixed(2) : 0;
  const total = +(subtotal + warrantyTotal).toFixed(2);
  const savingsTotal = +(savingsPerBand * qty).toFixed(2);

  const checkout = async (e) => {
    e.preventDefault();
    if (!buyer.name || !buyer.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/hume/checkout", {
        origin_url: window.location.origin,
        email: buyer.email,
        name: buyer.name,
        quantity: qty,
        warranty_10yr: warranty,
        ship_country: buyer.ship_country || null,
        notes: buyer.notes || null,
      });
      if (data?.url) { window.location.href = data.url; }
      else { toast.error("Couldn't reach checkout"); }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="hume-shop-page">
      <SiteNav />

      <section className="pt-28 pb-6 border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            Shop · Official Athlete Intelligence Assessment Device™
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]"
              data-testid="hume-shop-headline">
            The Hume Band.<br/>
            <span className="text-white/50">Continuous physiological intelligence, fused with your training.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1.1fr,0.9fr] gap-10">
        {/* Gallery */}
        <div>
          <div className="rounded-3xl overflow-hidden ring-1 ring-white/10 bg-black/40" data-testid="hume-shop-hero">
            <img src={activeImg} alt="Hume Band" className="w-full h-auto object-cover" />
          </div>
          <div className="mt-3 grid grid-cols-5 gap-2" data-testid="hume-shop-thumbs">
            {[HUME.images.hero, HUME.images.wrist, HUME.images.brief, HUME.images.battery, HUME.images.detail].map((src) => (
              <button key={src} type="button" onClick={() => setActiveImg(src)}
                className={`aspect-square overflow-hidden rounded-md ring-1 ${activeImg === src ? "ring-fuchsia-400" : "ring-white/10 hover:ring-white/30"}`}>
                <img src={src} alt="Hume Band thumbnail" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="hume-shop-badges">
            <div className="ink-card rounded-xl p-4 flex items-center gap-3">
              <Battery className="h-5 w-5 text-emerald-300 shrink-0" />
              <div className="text-xs text-white/75">Up to <b className="text-white">14-day</b> battery · 20-min fast charge</div>
            </div>
            <div className="ink-card rounded-xl p-4 flex items-center gap-3">
              <Waves className="h-5 w-5 text-cyan-300 shrink-0" />
              <div className="text-xs text-white/75"><b className="text-white">IP68</b> waterproof · up to 1 m for 2 h</div>
            </div>
            <div className="ink-card rounded-xl p-4 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-fuchsia-300 shrink-0" />
              <div className="text-xs text-white/75"><b className="text-white">1-year</b> warranty · 10-year upgrade at checkout</div>
            </div>
          </div>

          <div className="mt-8 ink-card rounded-2xl p-6" data-testid="hume-shop-specs">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3">Technical specifications</div>
            <ul className="grid sm:grid-cols-2 gap-y-2 text-sm text-white/85">
              {HUME.specs.map((s) => (
                <li key={s.k}><span className="text-white/50">{s.k}:</span> {s.v}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6 ink-card rounded-2xl p-6" data-testid="hume-shop-metrics">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3">45+ metrics streamed into your Athlete Intelligence Profile™</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {HUME.metrics.map((g) => (
                <div key={g.group}>
                  <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-300/70 font-bold mb-1">{g.group}</div>
                  <div className="text-sm text-white/75 leading-relaxed">{g.items.join(" · ")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Purchase card */}
        <form onSubmit={checkout} className="ink-card rounded-2xl p-6 sticky top-24 self-start" data-testid="hume-shop-buy-form">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-fuchsia-300/80 mb-2">
            Exclusive to BioHacker Sports™ members
          </div>
          <div className="flex items-baseline gap-3">
            <div className="font-display font-black text-4xl text-white tabular-nums" data-testid="hume-shop-price">
              ${unit.toFixed(2).replace(/\.00$/, "")}
            </div>
            <div className="text-white/40 line-through font-mono-num">${HUME.msrp_usd}</div>
            <div className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 uppercase tracking-wider" data-testid="hume-shop-tier-badge">
              {activeTier?.label || "Team-price"}
            </div>
          </div>
          <div className="mt-1 text-xs text-white/50">One-time purchase · No subscription required · Free worldwide shipping</div>
          {savingsPerBand > 0 && (
            <div className="mt-2 text-xs text-emerald-300 font-semibold" data-testid="hume-shop-tier-savings">
              You're saving ${savingsPerBand}/band · ${savingsTotal.toFixed(2)} on this order
            </div>
          )}

          {/* Tier ladder */}
          <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="hume-shop-tiers">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/45 mb-2">Bulk pricing</div>
            <ul className="grid grid-cols-2 gap-1.5 text-[11px]">
              {tiers.map((t) => {
                const active = t.min_qty === activeTier?.min_qty;
                return (
                  <li key={t.min_qty}
                      className={`flex items-center justify-between rounded-md px-2 py-1.5 ${active ? "bg-fuchsia-500/15 text-white font-semibold" : "text-white/60"}`}
                      data-testid={`hume-tier-row-${t.min_qty}`}>
                    <span>{t.min_qty}{t.max_qty ? `–${t.max_qty}` : "+"}</span>
                    <span className="font-mono-num tabular-nums">${t.unit_price_usd}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Quantity</Label>
              <div className="mt-1 flex items-center rounded-md border border-white/12 bg-white/[0.04]">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-3 py-2 text-white/70 hover:text-white" data-testid="hume-qty-minus">−</button>
                <input readOnly value={qty} className="w-full bg-transparent text-center text-white font-mono-num" data-testid="hume-qty-value" />
                <button type="button" onClick={() => setQty(Math.min(500, qty + 1))}
                  className="px-3 py-2 text-white/70 hover:text-white" data-testid="hume-qty-plus">+</button>
              </div>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Ship to</Label>
              <Input value={buyer.ship_country} onChange={(e) => setBuyer({ ...buyer, ship_country: e.target.value })}
                className="mt-1 bg-white/[0.04] border-white/12 text-white/90" data-testid="hume-buyer-country" />
            </div>
          </div>

          <label className="mt-4 flex items-start gap-2 rounded-md border border-white/12 bg-white/[0.02] p-3 cursor-pointer hover:bg-white/[0.04]"
                 data-testid="hume-warranty-toggle">
            <input type="checkbox" checked={warranty} onChange={(e) => setWarranty(e.target.checked)}
              className="mt-0.5 accent-fuchsia-500" />
            <div>
              <div className="text-sm text-white/90 font-semibold">Upgrade to 10-year warranty · +${warrantyPer}</div>
              <div className="text-xs text-white/50 mt-0.5">Complete hardware + sensor coverage for a decade · full replacement service</div>
            </div>
          </label>

          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Full name</Label>
              <Input required value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                className="mt-1 bg-white/[0.04] border-white/12 text-white/90" data-testid="hume-buyer-name" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Email (order confirmation)</Label>
              <Input required type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                className="mt-1 bg-white/[0.04] border-white/12 text-white/90" data-testid="hume-buyer-email" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Notes (optional)</Label>
              <Input value={buyer.notes} onChange={(e) => setBuyer({ ...buyer, notes: e.target.value })}
                placeholder="e.g. ship to school address, team of 12"
                className="mt-1 bg-white/[0.04] border-white/12 text-white/90" data-testid="hume-buyer-notes" />
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-5 border-t border-white/8 pt-4 space-y-1.5 text-sm" data-testid="hume-shop-breakdown">
            <Row label={`Hume Band × ${qty} @ $${unit}`} value={`$${subtotal.toFixed(2)}`} />
            {warranty && <Row label={`10-year warranty × ${qty}`} value={`$${warrantyTotal.toFixed(2)}`} />}
            <Row label="Shipping" value="Free" muted />
            <div className="flex items-center justify-between pt-2 border-t border-white/8">
              <span className="text-white font-bold">Total</span>
              <span className="text-white font-display font-black text-xl tabular-nums" data-testid="hume-shop-total">
                ${total.toFixed(2)} USD
              </span>
            </div>
          </div>

          <Button type="submit" disabled={busy}
            className="w-full mt-5 text-white font-bold text-sm py-6"
            style={{ backgroundColor: "var(--brand-secondary)" }}
            data-testid="hume-shop-checkout-button">
            {busy
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting to Stripe…</>
              : <>Checkout with Stripe <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>

          <ul className="mt-5 space-y-1.5 text-xs text-white/60" data-testid="hume-shop-trust">
            <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-300" /> 45-day money-back guarantee</li>
            <li className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-emerald-300" /> Free worldwide shipping · USPS · DHL · FedEx</li>
            <li className="flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-emerald-300" /> Data streams directly into BioHacker Intelligence Cloud™</li>
          </ul>
        </form>
      </section>

      {/* Why-buy strip */}
      <section className="border-t border-white/8 py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            Why Hume × BioHacker Sports™
          </div>
          <h2 className="font-display font-black text-3xl text-white tracking-tight">
            More than a wearable.<br/>
            <span className="text-white/50">The physiological input for every coaching decision.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl">{HUME.long}</p>
          <div className="mt-8">
            <Link to="/platform">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm">
                See how the platform uses it <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/70">{label}</span>
      <span className={`font-mono-num tabular-nums ${muted ? "text-white/50" : "text-white"}`}>{value}</span>
    </div>
  );
}
